import {MinusCircle} from 'lucide-react'
import React, {FC, useEffect, useId} from 'react'
import {createPortal} from 'react-dom'
import {useIntl} from 'react-intl'

import {SkeletonBlock, TextSkeleton} from './AllocationSkeleton'

type Props = {
  open: boolean
  title: string
  /** Paragraphs, in order. */
  messages: string[]
  confirmLabel: string
  onConfirm: () => void
  onCancel: () => void
  /** Server flow: the removal preview is loading — text skeleton, Confirm disabled. */
  loading?: boolean
  /** Server flow: the confirmed change is being saved — Confirm shows a spinner. */
  busy?: boolean
}

/**
 * Confirmation for every removal in Teacher Allocation (handover, 3C): nothing changes until the
 * user confirms; Cancel leaves the data as it was. Names and scope are filled in by the caller.
 */
const AllocationConfirmDialog: FC<Props> = ({
  open,
  title,
  messages,
  confirmLabel,
  onConfirm,
  onCancel,
  loading,
  busy,
}) => {
  const intl = useIntl()
  const titleId = useId()

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onCancel])

  if (!open) return null

  return createPortal(
    <div className='ta-dialog-root'>
      <div className='ta-dialog-backdrop' onClick={onCancel} aria-hidden />
      <div className='ta-dialog' role='alertdialog' aria-modal='true' aria-labelledby={titleId}>
        <div className='ta-dialog__head'>
          <span className='ta-dialog__icon'>
            <MinusCircle size={20} aria-hidden />
          </span>
          <h2 id={titleId}>{loading ? <SkeletonBlock width={260} height={20} /> : title}</h2>
        </div>
        {loading ? (
          <div className='ta-dialog__text'>
            <TextSkeleton lines={3} />
          </div>
        ) : (
          messages.map((message, idx) => (
            <p key={idx} className='ta-dialog__text'>
              {message}
            </p>
          ))
        )}
        <div className='ta-dialog__actions'>
          <button type='button' className='ta-btn ta-btn--ghost' onClick={onCancel} autoFocus>
            {intl.formatMessage({id: 'ONBOARDING_WIZARD.TEACHER_ALLOCATION.CANCEL'})}
          </button>
          <button
            type='button'
            className='ta-btn ta-btn--danger'
            onClick={onConfirm}
            disabled={loading || busy}
          >
            {busy && <span className='spinner-border spinner-border-sm' aria-hidden />}
            {loading ? <SkeletonBlock width={120} height={14} /> : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default AllocationConfirmDialog
